// Selector-list helpers shared by observed parsing and root inheritance.
// Keeps comma splitting and prefix checks independent from leaf collection.

// Split selector list by top-level commas, preserving nested function scopes.
export function splitTopLevelSelectorList(selector: string): string[] {
  const parts: string[] = []
  let current = ''
  let depthParen = 0
  let depthBracket = 0
  let quote: '"' | "'" | null = null

  // Scan selector chars and split only on top-level commas.
  for (let index = 0; index < selector.length; index += 1) {
    const char = selector[index]
    const previous = index > 0 ? selector[index - 1] : ''

    if (quote) {
      if (char === quote && previous !== '\\') quote = null
      current += char
      continue
    } else if (char === '"' || char === "'") {
      quote = char
    } else if (char === '(') {
      depthParen += 1
    } else if (char === ')' && depthParen > 0) {
      depthParen -= 1
    } else if (char === '[') {
      depthBracket += 1
    } else if (char === ']' && depthBracket > 0) {
      depthBracket -= 1
    } else {
      // Keep depth counters unchanged for regular selector chars.
    }

    if (char === ',' && depthParen === 0 && depthBracket === 0) {
      parts.push(current)
      current = ''
      continue
    }

    current += char
  }

  if (current.length > 0) parts.push(current)
  return parts
}

// Join selector-list parts with the renderer's stable comma spacing.
export function joinSelectorParts(parts: string[]): string {
  return parts.map((part) => part.trim()).filter(Boolean).join(', ')
}

// Normalize equivalent selector spacing before observed root matching.
export function normalizeSelectorForRootMatch(selector: string): string {
  // Matches whitespace around CSS combinators. Example match: " > ".
  const compactCombinators = selector.trim().replace(/\s*([>+~])\s*/g, '$1')
  // Matches repeated selector whitespace. Example match: "  ".
  return compactCombinators.replace(/\s+/g, ' ')
}

// Check whether a selector starts at an observed root boundary.
export function selectorStartsWithRoot(selector: string, root: string): boolean {
  const normalizedSelector = normalizeSelectorForRootMatch(selector)
  const normalizedRoot = normalizeSelectorForRootMatch(root)

  if (normalizedSelector === normalizedRoot) return true
  if (!normalizedSelector.startsWith(normalizedRoot)) return false

  const nextChar = normalizedSelector[normalizedRoot.length]
  return isRootBoundary(nextChar)
}

// Guard against `.a` accidentally matching `.abc`.
function isRootBoundary(char: string | undefined): boolean {
  if (!char) return true
  return char === ' '
    || char === '>'
    || char === '+'
    || char === '~'
    || char === '.'
    || char === '#'
    || char === ':'
    || char === '['
}
